import { Mat4, Vec3 } from 'playcanvas'
import { GsplatShaderEffect } from './gsplat-shader-effect.js'

// Match the current 3dgs_web entrance: keep every splat visible as a
// screen-sized point first, then restore the original Gaussian shape with a
// radial front expanding from the model focus.
const introRevealGLSL = /* glsl */ `
uniform float uTime;
uniform float uTotalDuration;
uniform vec3 uCameraPosLocal;
uniform float uPointPixels;
uniform float uFocalScale;
uniform float uGaussianDiameterFactor;
uniform float uPointHoldRatio;
uniform float uRestoreEndRatio;
uniform float uOpacityBoost;
uniform float uEndRadius;
uniform vec3 uBoundsCenter;

float getTimelineProgress() {
    return clamp(uTime / max(uTotalDuration, 0.001), 0.0, 1.0);
}

float getRestoreProgress() {
    float progress = getTimelineProgress();
    return clamp(
        (progress - uPointHoldRatio) /
        max(uRestoreEndRatio - uPointHoldRatio, 0.001),
        0.0,
        1.0
    );
}

float getRestoreFront() {
    float progress = getRestoreProgress();
    return max(uEndRadius, 0.001) * (0.85 * progress + 0.15 * progress * progress);
}

float getPointBlend(vec3 center) {
    if (getTimelineProgress() <= uPointHoldRatio) {
        return 1.0;
    }
    float distanceFromCenter = length(center - uBoundsCenter);
    return distanceFromCenter > getRestoreFront() ? 1.0 : 0.0;
}

float getScreenPointSize(vec3 center) {
    float viewDistance = max(length(center - uCameraPosLocal), 0.05);
    return (uPointPixels * viewDistance)
        / (max(uFocalScale, 1.0) * max(uGaussianDiameterFactor, 0.001));
}

void modifySplatCenter(inout vec3 center) {
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    vec3 originalScale = scale;
    vec3 pointScale = originalScale;
    gsplatMakeSpherical(pointScale, max(getScreenPointSize(modifiedCenter), 0.00001));
    scale = getPointBlend(originalCenter) > 0.5 ? pointScale : originalScale;
}

void modifySplatColor(vec3 center, inout vec4 color) {
    float pointBlend = getPointBlend(center);
    vec4 pointColor = color;
    float sourceAlpha = clamp(pointColor.a, 0.0, 1.0);
    // Match test-oss: strengthen real splats without lifting transparent
    // background splats, which otherwise disappear in a flash at teardown.
    pointColor.a = 1.0 - pow(1.0 - sourceAlpha, max(uOpacityBoost, 0.001));
    float luma = dot(pointColor.rgb, vec3(0.299, 0.587, 0.114));
    pointColor.rgb = clamp(mix(vec3(luma), pointColor.rgb, 1.1), 0.0, 1.5);
    if (pointBlend > 0.5) {
        color = pointColor;
    }
}
`

const introRevealWGSL = /* wgsl */ `
uniform uTime: f32;
uniform uTotalDuration: f32;
uniform uCameraPosLocal: vec3f;
uniform uPointPixels: f32;
uniform uFocalScale: f32;
uniform uGaussianDiameterFactor: f32;
uniform uPointHoldRatio: f32;
uniform uRestoreEndRatio: f32;
uniform uOpacityBoost: f32;
uniform uEndRadius: f32;
uniform uBoundsCenter: vec3f;

fn getTimelineProgress() -> f32 {
    return clamp(uniform.uTime / max(uniform.uTotalDuration, 0.001), 0.0, 1.0);
}

fn getRestoreProgress() -> f32 {
    let progress = getTimelineProgress();
    return clamp(
        (progress - uniform.uPointHoldRatio) /
        max(uniform.uRestoreEndRatio - uniform.uPointHoldRatio, 0.001),
        0.0,
        1.0
    );
}

fn getRestoreFront() -> f32 {
    let progress = getRestoreProgress();
    return max(uniform.uEndRadius, 0.001) * (0.85 * progress + 0.15 * progress * progress);
}

fn getPointBlend(center: vec3f) -> f32 {
    if (getTimelineProgress() <= uniform.uPointHoldRatio) {
        return 1.0;
    }
    let distanceFromCenter = length(center - uniform.uBoundsCenter);
    return select(0.0, 1.0, distanceFromCenter > getRestoreFront());
}

fn getScreenPointSize(center: vec3f) -> f32 {
    let viewDistance = max(length(center - uniform.uCameraPosLocal), 0.05);
    return (uniform.uPointPixels * viewDistance)
        / (max(uniform.uFocalScale, 1.0) * max(uniform.uGaussianDiameterFactor, 0.001));
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    let originalScale = *scale;
    var pointScale = originalScale;
    gsplatMakeSpherical(&pointScale, max(getScreenPointSize(modifiedCenter), 0.00001));
    *scale = select(originalScale, pointScale, getPointBlend(originalCenter) > 0.5);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    let pointBlend = getPointBlend(center);
    var pointColor = *color;
    let sourceAlpha = clamp(pointColor.a, 0.0, 1.0);
    // Match test-oss: preserve transparent/background splats.
    pointColor.a = 1.0 - pow(1.0 - sourceAlpha, max(uniform.uOpacityBoost, 0.001));
    let luma = dot(pointColor.rgb, vec3f(0.299, 0.587, 0.114));
    pointColor = vec4f(clamp(mix(vec3f(luma), pointColor.rgb, 1.1), vec3f(0.0), vec3f(1.5)), pointColor.a);
    if (pointBlend > 0.5) {
        *color = pointColor;
    }
}
`

export class GsplatEffectIntroReveal extends GsplatShaderEffect {
  static scriptName = 'gsplatEffectIntroReveal'

  // Keep the source branch's public preset fields. Some are retained so the
  // entrance factory can share the same configuration contract even though
  // the current point-to-Gaussian shader only consumes the fields below.
  thresholdFeather = 0.00002
  initialDensity = 0.00025
  densityExponent = 3.2
  introAppearRatio = 0.26
  introAppearExponent = 1.35
  particleStartSize = 0.0035
  particleGrowExponent = 1.8
  shapeRestoreStart = 0.72
  shapeRestoreEnd = 0.96
  totalDuration = 4.8
  rippleStrength = 0.82
  rippleFrequency = 3.2
  rippleWidth = 0.13
  rippleTravel = 1.18
  cameraEntity = null
  pointPixels = 0.82
  focalScale = 900
  renderPixelRatio = 1
  entranceMinPixelSize = 0.5
  gaussianDiameterFactor = 5.656854249
  pointHoldRatio = 0.4
  restoreEndRatio = 0.96
  restoreFeather = 0.075
  opacityBoost = 1.35
  endRadius = 1.7320508
  bandWidth = 0.07
  liftAmount = 0.0008
  waveTint = new Vec3(0.08, 0.1, 0.14)
  boundsCenter = new Vec3(0, 0, 0)
  boundsHalfExtents = new Vec3(1, 1, 1)
  coverageGrid = new Vec3(5, 5, 5)
  detailGrid = new Vec3(9, 9, 9)

  _cameraPosLocal = new Vec3()
  _inverseWorld = new Mat4()
  _cameraPosLocalArray = [0, 0, 0]
  _boundsCenterArray = [0, 0, 0]
  _originalMinPixelSize = null

  getShaderGLSL() {
    return introRevealGLSL
  }

  getShaderWGSL() {
    return introRevealWGSL
  }

  applyShaderToMaterial(material) {
    if (this._originalMinPixelSize === null) {
      const originalMinPixelSize = Number(material.getParameter('minPixelSize')?.data)
      this._originalMinPixelSize = Number.isFinite(originalMinPixelSize)
        ? originalMinPixelSize
        : 2
    }
    material.setParameter(
      'minPixelSize',
      Math.min(this._originalMinPixelSize, this.entranceMinPixelSize)
    )
    super.applyShaderToMaterial(material)
    this.updateEffect(0)
    material.update()
  }

  removeShaders() {
    const material = this.material
    if (material && this._originalMinPixelSize !== null) {
      material.setParameter('minPixelSize', this._originalMinPixelSize)
    }
    this._originalMinPixelSize = null
    super.removeShaders()
  }

  updateEffect(effectTime) {
    if (this.shouldStopEffect()) {
      this.enabled = false
      return
    }

    this.setUniform('uTime', effectTime)
    this.setUniform('uTotalDuration', this.totalDuration)

    if (this.cameraEntity?.getPosition && this.entity?.getWorldTransform) {
      const cameraWorldPosition = this.cameraEntity.getPosition()
      this._inverseWorld.copy(this.entity.getWorldTransform()).invert()
      this._inverseWorld.transformPoint(cameraWorldPosition, this._cameraPosLocal)
      this._cameraPosLocalArray[0] = this._cameraPosLocal.x
      this._cameraPosLocalArray[1] = this._cameraPosLocal.y
      this._cameraPosLocalArray[2] = this._cameraPosLocal.z

      const canvas = this.app?.graphicsDevice?.canvas
      const cssCanvasHeight = Math.max(
        Number(canvas?.clientHeight) || Number(canvas?.height) || 720,
        1
      )
      const renderCanvasHeight = Math.max(Number(canvas?.height) || cssCanvasHeight, 1)
      const halfFovRadians = ((Number(this.cameraEntity.camera?.fov) || 45) * Math.PI / 180) * 0.5
      this.focalScale = renderCanvasHeight * 0.5 / Math.max(Math.tan(halfFovRadians), 0.0001)
      this.renderPixelRatio = Math.min(
        4,
        Math.max(0.5, renderCanvasHeight / cssCanvasHeight)
      )
    }

    this.setUniform('uCameraPosLocal', this._cameraPosLocalArray)
    this.setUniform('uPointPixels', this.pointPixels * this.renderPixelRatio)
    this.setUniform('uFocalScale', this.focalScale)
    this.setUniform('uGaussianDiameterFactor', this.gaussianDiameterFactor)
    this.setUniform('uPointHoldRatio', this.pointHoldRatio)
    this.setUniform('uRestoreEndRatio', this.restoreEndRatio)
    this.setUniform('uOpacityBoost', this.opacityBoost)
    this.setUniform('uEndRadius', this.endRadius)

    this._boundsCenterArray[0] = this.boundsCenter.x
    this._boundsCenterArray[1] = this.boundsCenter.y
    this._boundsCenterArray[2] = this.boundsCenter.z
    this.setUniform('uBoundsCenter', this._boundsCenterArray)
  }

  isEffectComplete() {
    return this.effectTime >= this.totalDuration
  }
}
